import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Phone,
  Message,
  Settings,
  AccountCircle,
  Logout,
  ExpandLess,
  ExpandMore,
  Build,
  SmsOutlined,
  WorkHistory,
  PhoneInTalk,
  FileUpload,
  Group,
  CallMade,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';

const drawerWidth = 240;

const getMenuItems = (user: any) => {
  const baseItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { 
      text: 'Aigent', 
      icon: <Phone />, 
      path: '/calls',
      submenu: [
        { text: 'Agents', icon: <Group />, path: '/aigent/agents' },
        { text: 'All Campaigns', icon: <CallMade />, path: '/aigent/campaigns' },
        { text: 'CSV Uploads', icon: <FileUpload />, path: '/calls/csv-uploads' },
      ]
    },
    { 
      text: 'Twilio Exporter', 
      icon: <Message />, 
      path: '/twilio',
      submenu: [
        { text: 'Configuration', icon: <Build />, path: '/twilio' },
        { text: 'Calls', icon: <PhoneInTalk />, path: '/twilio/calls' },
        { text: 'SMS', icon: <SmsOutlined />, path: '/twilio/sms' },
        { text: 'Job Runs', icon: <WorkHistory />, path: '/twilio/job-runs' },
      ]
    },
    // { text: 'Prompt Generator', icon: <AutoAwesome />, path: '/prompts' }, // Hidden - to be rebuilt later
    { text: 'Settings', icon: <Settings />, path: '/settings' },
  ];

  // Add Admin menu for narayana user only
  if (user?.username === 'narayana') {
    baseItems.splice(-1, 0, { text: 'Admin', icon: <AdminPanelSettings />, path: '/admin' });
  }

  return baseItems;
};

export const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedMenus, setExpandedMenus] = useState<{ [key: string]: boolean }>({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: any) => state.auth);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleSubmenuToggle = (menuText: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuText]: !prev[menuText]
    }));
  };

  const isSubmenuActive = (submenu: any[]) => {
    return submenu.some(item => location.pathname === item.path);
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
        HubOne Tools
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {getMenuItems(user).map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path || (item.submenu && isSubmenuActive(item.submenu))}
                onClick={() => {
                  if (item.submenu) {
                    // Navigate to main page first, then toggle submenu
                    handleNavigation(item.path);
                    handleSubmenuToggle(item.text);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'primary.main',
                      fontWeight: 600,
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem'
                  }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '0.95rem',
                      fontWeight: 500
                    }
                  }}
                />
                {item.submenu && (
                  expandedMenus[item.text] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>
            {item.submenu && (
              <Collapse in={expandedMenus[item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.submenu.map((subItem) => (
                    <ListItem key={subItem.text} disablePadding>
                      <ListItemButton
                        sx={{ 
                          pl: 4,
                          ml: 1,
                          borderLeft: '2px solid',
                          borderLeftColor: 'primary.main',
                          backgroundColor: location.pathname === subItem.path ? 'action.selected' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                            borderLeftColor: 'primary.dark',
                          }
                        }}
                        selected={location.pathname === subItem.path}
                        onClick={() => handleNavigation(subItem.path)}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 36,
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem',
                            color: location.pathname === subItem.path ? 'primary.main' : 'text.secondary'
                          }
                        }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subItem.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                              fontWeight: location.pathname === subItem.path ? 500 : 400,
                              color: location.pathname === subItem.path ? 'primary.main' : 'text.primary'
                            }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {getMenuItems(user).find(item => item.path === location.pathname)?.text || 'HubOne Tools'}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
